import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../lib/api'
import { keys, useDraft, useDraftValidation, useSaveDraft } from '../lib/queries'
import type { DraftOut, DraftPayload, DraftValidation, SubmitResponse } from '../lib/types'

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

export interface WizardContextValue {
  isLoading: boolean
  loadError: ApiError | null
  refetchDraft: () => void

  payload: DraftPayload
  /** Merges a partial step-1 patch and schedules an autosave. */
  updatePersonal: (patch: DraftPayload['personal']) => void

  /** Save immediately. Never validates — a draft may be as incomplete as the user likes. */
  saveNow: (step?: number) => Promise<void>

  saveState: SaveState
  lastSavedAt: string | null
  /**
   * The draft was changed in another window. We refuse to overwrite it and
   * offer a reload instead — silently letting the stale tab win erases work.
   */
  conflict: boolean
  dismissConflict: () => void

  validation: DraftValidation | undefined
  refetchValidation: () => void

  /** Set on a successful submit so /wizard/done has something to show. */
  submission: SubmitResponse | null
  setSubmission: (response: SubmitResponse) => void
}

const WizardContext = createContext<WizardContextValue | null>(null)

export function useWizard(): WizardContextValue {
  const value = useContext(WizardContext)
  if (!value) throw new Error('useWizard must be used inside <WizardProvider>')
  return value
}

const AUTOSAVE_DEBOUNCE_MS = 800

export function WizardProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const draftQuery = useDraft()
  const validationQuery = useDraftValidation()
  const saveMutation = useSaveDraft()

  const [payload, setPayload] = useState<DraftPayload>({})
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)
  const [submission, setSubmissionState] = useState<SubmitResponse | null>(null)
  const [hydrated, setHydrated] = useState(false)

  const payloadRef = useRef<DraftPayload>({})
  const stepRef = useRef(1)
  const timerRef = useRef<number | null>(null)
  const inFlightRef = useRef(false)
  const queuedRef = useRef(false)

  // Hydrate once. Refetching under a form the user is typing in would fight
  // them, so the server copy is authoritative only at load and after a
  // deliberate reload.
  useEffect(() => {
    if (hydrated || !draftQuery.data) return
    payloadRef.current = draftQuery.data.payload ?? {}
    stepRef.current = draftQuery.data.current_step ?? 1
    setPayload(payloadRef.current)
    setLastSavedAt(draftQuery.data.last_saved_at)
    setHydrated(true)
  }, [draftQuery.data, hydrated])

  const runSave = useCallback(
    async (step?: number): Promise<void> => {
      if (conflict) return
      if (step) stepRef.current = step

      if (inFlightRef.current) {
        queuedRef.current = true
        return
      }
      inFlightRef.current = true
      setSaveState('saving')

      const current = queryClient.getQueryData<DraftOut>(keys.draft)
      const version = current?.version ?? draftQuery.data?.version ?? 1

      try {
        const saved = await saveMutation.mutateAsync({
          currentStep: stepRef.current,
          payload: payloadRef.current,
          version,
        })
        setLastSavedAt(saved.last_saved_at)
        setSaveState('saved')
      } catch (cause) {
        if (cause instanceof ApiError && cause.isStaleDraft) {
          setConflict(true)
          setSaveState('error')
        } else {
          setSaveState('error')
        }
      } finally {
        inFlightRef.current = false
        if (queuedRef.current) {
          queuedRef.current = false
          void runSave()
        }
      }
    },
    [conflict, draftQuery.data, queryClient, saveMutation],
  )

  const scheduleSave = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      void runSave()
    }, AUTOSAVE_DEBOUNCE_MS)
  }, [runSave])

  const updatePersonal = useCallback<WizardContextValue['updatePersonal']>(
    (patch) => {
      const next: DraftPayload = {
        ...payloadRef.current,
        personal: {
          ...payloadRef.current.personal,
          ...patch,
          address: { ...payloadRef.current.personal?.address, ...patch?.address },
        },
      }
      // Belt and braces: the server strips this too, but an Aadhaar number has
      // no business in a plain JSONB column and should never leave the browser
      // on this request in the first place.
      if (next.personal && 'aadhaar' in next.personal) {
        delete (next.personal as Record<string, unknown>).aadhaar
      }
      payloadRef.current = next
      setPayload(next)
      scheduleSave()
    },
    [scheduleSave],
  )

  const saveNow = useCallback(
    async (step?: number) => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      await runSave(step)
    },
    [runSave],
  )

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    },
    [],
  )

  const refetchDraft = useCallback(() => {
    setConflict(false)
    setHydrated(false)
    void draftQuery.refetch()
    void validationQuery.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo<WizardContextValue>(
    () => ({
      // Still "loading" until the hydrate effect has copied the draft into
      // `payload`. Child effects run before this provider's, so a step that
      // mounts the moment the query resolves would otherwise read an empty
      // payload, hydrate its form from it, and autosave blanks over the draft.
      isLoading: draftQuery.isLoading || (!!draftQuery.data && !hydrated),
      loadError: draftQuery.error ?? null,
      refetchDraft,
      payload,
      updatePersonal,
      saveNow,
      saveState,
      lastSavedAt,
      conflict,
      dismissConflict: () => setConflict(false),
      validation: validationQuery.data,
      refetchValidation: () => void validationQuery.refetch(),
      submission,
      setSubmission: setSubmissionState,
    }),
    [
      draftQuery.isLoading,
      draftQuery.data,
      hydrated,
      draftQuery.error,
      refetchDraft,
      payload,
      updatePersonal,
      saveNow,
      saveState,
      lastSavedAt,
      conflict,
      validationQuery.data,
      validationQuery,
      submission,
    ],
  )

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
}
